import React, { useEffect, useRef, useState } from 'react';
import { Box, Heading, Spinner, Text, HStack, Badge, Flex, Select } from '@chakra-ui/react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Interfaz para los datos de denuncias por zona
export interface DenunciaPorZona {
  id: string;
  direccion: string;
  localidad: string;
  provincia: string;
  codigoPostal: string;
  estado: string;
  longitud?: number;
  latitud?: number;
}

interface MapaZonasDenunciasProps {
  denuncias: DenunciaPorZona[];
  isLoading: boolean;
}

const MapaZonasDenuncias: React.FC<MapaZonasDenunciasProps> = ({ denuncias, isLoading }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [denunciasConCoordenadas, setDenunciasConCoordenadas] = useState<DenunciaPorZona[]>([]);
  const [geocodificando, setGeocod] = useState<boolean>(false);
  
  // Token público de Mapbox
  mapboxgl.accessToken = 'pk.eyJ1IjoiamF2aWVyZm9udCIsImEiOiJjbTk5djd2bmMwZ3BvMnJzYjE3NDExdGRyIn0.AZrIu2NmMPk_XpqWo_43Mw';

  // Función para obtener color según estado
  const getColorByEstado = (estado: string): string => {
    switch (estado.toLowerCase()) {
      case 'resuelto':
        return '#38A169'; // Verde
      case 'en trámite':
        return '#DD6B20'; // Naranja
      case 'pendiente':
        return '#E53E3E'; // Rojo
      default:
        return '#718096'; // Gris
    }
  };

  // Función para geocodificar una dirección usando Mapbox Geocoding API
  const geocodificarDireccion = async (direccion: string): Promise<{ longitud: number; latitud: number } | null> => {
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(direccion)}.json?access_token=${mapboxgl.accessToken}&limit=1&country=es`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [longitud, latitud] = data.features[0].center;
        return { longitud, latitud };
      }
      
      return null;
    } catch (error) {
      console.error('Error al geocodificar dirección:', error);
      return null;
    }
  };

  // Geocodificar denuncias cuando cambian
  useEffect(() => {
    const geocodificarDenuncias = async () => {
      if (denuncias.length === 0) {
        setDenunciasConCoordenadas([]);
        return;
      }

      setGeocod(true);
      
      try {
        // Procesar solo las primeras 50 denuncias para evitar límites de API
        const denunciasParaProcesar = denuncias.slice(0, 50);
        
        const denunciasGeocod = await Promise.all(
          denunciasParaProcesar.map(async (denuncia) => {
            // Si ya tiene coordenadas, devolverla tal cual
            if (denuncia.longitud && denuncia.latitud) {
              return denuncia;
            }

            // Si no tiene dirección válida, devolverla sin coordenadas
            if (!denuncia.direccion || denuncia.direccion === 'Dirección no disponible') {
              return denuncia;
            }

            // Geocodificar la dirección
            const direccionCompleta = `${denuncia.direccion}, ${denuncia.localidad}, ${denuncia.provincia}`;
            const coordenadas = await geocodificarDireccion(direccionCompleta);

            if (coordenadas) {
              return {
                ...denuncia,
                longitud: coordenadas.longitud,
                latitud: coordenadas.latitud
              };
            }

            return denuncia;
          })
        );

        setDenunciasConCoordenadas(denunciasGeocod);
      } catch (error) {
        console.error('Error al geocodificar denuncias:', error);
        setDenunciasConCoordenadas(denuncias);
      } finally {
        setGeocod(false);
      }
    };

    geocodificarDenuncias();
  }, [denuncias]);

  // Inicializar y configurar el mapa
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-3.7038, 40.4168], // Madrid como centro inicial
      zoom: 5
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Limpiar al desmontar
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Actualizar marcadores cuando cambian las denuncias geocodificadas o el filtro
  useEffect(() => {
    if (!map.current || geocodificando || denunciasConCoordenadas.length === 0) return;

    // Eliminar marcadores existentes
    const markers = document.getElementsByClassName('mapboxgl-marker');
    while (markers[0]) {
      markers[0].remove();
    }

    // Filtrar denuncias según el estado seleccionado
    const denunciasFiltradas = filtroEstado === 'todos' 
      ? denunciasConCoordenadas 
      : denunciasConCoordenadas.filter(denuncia => 
          denuncia.estado.toLowerCase() === filtroEstado.toLowerCase()
        );
    
    // Si no hay denuncias filtradas, no hacer nada
    if (denunciasFiltradas.length === 0) return;

    // Coordenadas para ajustar la vista
    const bounds = new mapboxgl.LngLatBounds();
    let validCoordinates = false;

    // Añadir marcadores para cada denuncia
    denunciasFiltradas.forEach(denuncia => {
      if (denuncia.longitud && denuncia.latitud) {
        validCoordinates = true;
        
        // Crear elemento para el marcador
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundColor = getColorByEstado(denuncia.estado);
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.borderRadius = '50%';
        el.style.border = '2px solid white';
        el.style.boxShadow = '0 0 5px rgba(0,0,0,0.3)';
        el.style.cursor = 'pointer';
        
        // Crear popup con información
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(`
            <strong>ID: ${denuncia.id}</strong><br/>
            <strong>Estado: ${denuncia.estado}</strong><br/>
            Dirección: ${denuncia.direccion}<br/>
            ${denuncia.localidad}, ${denuncia.provincia} ${denuncia.codigoPostal}
          `);
        
        // Añadir marcador al mapa
        new mapboxgl.Marker(el)
          .setLngLat([denuncia.longitud, denuncia.latitud])
          .setPopup(popup)
          .addTo(map.current!);
        
        // Extender los límites para incluir este punto
        bounds.extend([denuncia.longitud, denuncia.latitud]);
      }
    });

    // Ajustar la vista del mapa para mostrar todos los marcadores
    if (validCoordinates) {
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
        duration: 1000
      });
    }
  }, [denunciasConCoordenadas, filtroEstado, geocodificando]);

  // Renderizar estadísticas de denuncias por estado
  const renderEstadisticas = () => {
    if (denunciasConCoordenadas.length === 0) return null;

    const resueltas = denunciasConCoordenadas.filter(d => d.estado.toLowerCase() === 'resuelto').length;
    const enTramite = denunciasConCoordenadas.filter(d => d.estado.toLowerCase() === 'en trámite').length;
    const pendientes = denunciasConCoordenadas.filter(d => d.estado.toLowerCase() === 'pendiente').length;
    const total = denunciasConCoordenadas.length;
    const conCoordenadas = denunciasConCoordenadas.filter(d => d.longitud && d.latitud).length;

    return (
      <HStack spacing={4} mt={4} wrap="wrap">
        <Badge colorScheme="green" p={2} borderRadius="md">
          Resueltas: {resueltas} ({Math.round((resueltas / total) * 100)}%)
        </Badge>
        <Badge colorScheme="orange" p={2} borderRadius="md">
          En trámite: {enTramite} ({Math.round((enTramite / total) * 100)}%)
        </Badge>
        <Badge colorScheme="red" p={2} borderRadius="md">
          Pendientes: {pendientes} ({Math.round((pendientes / total) * 100)}%)
        </Badge>
        <Badge colorScheme="gray" p={2} borderRadius="md">
          Total: {total}
        </Badge>
        <Badge colorScheme="blue" p={2} borderRadius="md">
          Con ubicación: {conCoordenadas}
        </Badge>
      </HStack>
    );
  };

  return (
    <Box p={4} bg="white" borderRadius="md" boxShadow="sm">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Mapa de Zonas con Denuncias</Heading>
        <Select 
          value={filtroEstado} 
          onChange={(e) => setFiltroEstado(e.target.value)}
          width="200px"
        >
          <option value="todos">Todos los estados</option>
          <option value="resuelto">Resueltas</option>
          <option value="en trámite">En trámite</option>
          <option value="pendiente">Pendientes</option>
        </Select>
      </Flex>
      
      {renderEstadisticas()}
      
      {/* Contenedor del mapa siempre presente */}
      <Box position="relative" mt={4}>
        <Box 
          ref={mapContainer} 
          height="500px" 
          width="100%" 
          borderRadius="md" 
          overflow="hidden"
          bg="gray.100"
        />
        
        {/* Overlay de carga */}
        {isLoading && (
          <Flex 
            position="absolute" 
            top={0} 
            left={0} 
            right={0} 
            bottom={0} 
            bg="whiteAlpha.900" 
            justify="center" 
            align="center" 
            direction="column"
            borderRadius="md"
          >
            <Spinner size="xl" color="red.500" thickness="4px" />
            <Text mt={4}>Cargando denuncias...</Text>
          </Flex>
        )}
        
        {/* Overlay de geocodificación */}
        {!isLoading && geocodificando && (
          <Flex 
            position="absolute" 
            top={0} 
            left={0} 
            right={0} 
            bottom={0} 
            bg="whiteAlpha.900" 
            justify="center" 
            align="center" 
            direction="column"
            borderRadius="md"
          >
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text mt={4}>Geocodificando direcciones...</Text>
            <Text fontSize="sm" color="gray.600" mt={2}>
              Esto puede tardar unos momentos
            </Text>
          </Flex>
        )}
        
        {/* Overlay de sin datos */}
        {!isLoading && !geocodificando && denunciasConCoordenadas.length === 0 && (
          <Flex 
            position="absolute" 
            top={0} 
            left={0} 
            right={0} 
            bottom={0} 
            bg="whiteAlpha.900" 
            justify="center" 
            align="center"
            borderRadius="md"
          >
            <Text>No hay datos disponibles</Text>
          </Flex>
        )}
      </Box>
      
      <Box mt={4} p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" color="gray.600">
          Este mapa muestra la distribución geográfica de las denuncias. 
          Los colores indican el estado: verde (resueltas), naranja (en trámite) y rojo (pendientes).
          Haga clic en los marcadores para ver más detalles.
        </Text>
      </Box>
    </Box>
  );
};

export default MapaZonasDenuncias;
